import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Menu, 
  User, 
  Settings, 
  LogOut, 
  Bell,
  Search,
  HelpCircle
} from 'lucide-react';

const Header = ({ onToggleSidebar = () => {}, isSidebarOpen = false }) => {
  const { account, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const location = useLocation();

  const toggleUserMenu = () => setShowUserMenu(!showUserMenu);
  const toggleNotifications = () => setShowNotifications(!showNotifications);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const getPageTitle = () => {
    const path = location.pathname;
    switch (path) {
      case '/':
      case '/dashboard':
        return 'Dashboard';
      case '/documents':
        return 'Documents';
      case '/products':
        return 'Products';
      case '/chat':
        return 'Chat';
      case '/configurations':
        return 'Configurations';
      default:
        return 'Home';
    }
  };

  return (
    <header className="bg-white border-bottom shadow-sm" style={{ zIndex: 1000 }}>
      <div className="container-fluid">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 py-3">
          {/* Left side - Logo and breadcrumb */}
          <div className="d-flex align-items-center flex-wrap gap-3">
            <button
              className="btn btn-outline-primary d-inline-flex d-lg-none"
              onClick={onToggleSidebar}
              aria-label={isSidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
            >
              <Menu size={18} />
            </button>
            <div className="me-1">
              <img 
                src="/logo.png" 
                alt="AI Tech Assistant Logo" 
                style={{ height: '40px', width: 'auto' }}
                className="d-inline-block align-top"
              />
            </div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard" className="text-decoration-none">
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {getPageTitle()}
                </li>
              </ol>
            </nav>
          </div>

          {/* Right side - Search, notifications, user menu */}
          <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end flex-grow-1">
            {/* Search */}
            <div className="position-relative d-none d-lg-block">
              <Search className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={18} />
              <input
                type="text"
                className="form-control ps-4"
                placeholder="Search..."
                style={{ width: '250px' }}
              />
            </div>

            {/* Notifications */}
            <div className="position-relative">
              <button
                className="btn btn-outline-secondary position-relative"
                onClick={toggleNotifications}
                aria-expanded={showNotifications}
                aria-label="Notifications"
              >
                <Bell size={18} />
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                  3
                </span>
              </button>

              {/* Notifications dropdown */}
              {showNotifications && (
                <div className="dropdown-menu show position-absolute end-0 mt-2" style={{ width: '320px', zIndex: 1001 }}>
                  <div className="dropdown-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Notifications</h6>
                    <button className="btn btn-sm btn-link text-decoration-none">Mark all read</button>
                  </div>
                  <div className="dropdown-divider"></div>
                  
                  {/* Notification items */}
                  <div className="px-3 py-2">
                    <div className="d-flex align-items-start mb-2">
                      <div className="bg-primary rounded-circle p-1 me-2">
                        <Bell size={12} className="text-white" />
                      </div>
                      <div className="flex-grow-1">
                        <div className="small fw-bold">Document processed</div>
                        <div className="small text-muted">report.pdf has been successfully indexed</div>
                        <div className="small text-muted">2 minutes ago</div>
                      </div>
                    </div>
                    
                    <div className="d-flex align-items-start mb-2">
                      <div className="bg-success rounded-circle p-1 me-2">
                        <Bell size={12} className="text-white" />
                      </div>
                      <div className="flex-grow-1">
                        <div className="small fw-bold">AI service connected</div>
                        <div className="small text-muted">OpenAI API is now available</div>
                        <div className="small text-muted">1 hour ago</div>
                      </div>
                    </div>
                    
                    <div className="d-flex align-items-start">
                      <div className="bg-warning rounded-circle p-1 me-2">
                        <Bell size={12} className="text-white" />
                      </div>
                      <div className="flex-grow-1">
                        <div className="small fw-bold">Storage warning</div>
                        <div className="small text-muted">Upload directory is 80% full</div>
                        <div className="small text-muted">3 hours ago</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="dropdown-divider"></div>
                  <div className="px-3 py-2">
                    <Link to="/notifications" className="btn btn-outline-primary btn-sm w-100">
                      View all notifications
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Help */}
            <button className="btn btn-outline-secondary d-none d-sm-inline-flex" title="Help">
              <HelpCircle size={18} />
            </button>

            {/* User menu */}
            <div className="position-relative">
              <button
                className="btn btn-outline-primary d-flex align-items-center gap-2"
                onClick={toggleUserMenu}
                aria-expanded={showUserMenu}
                aria-label="User menu"
              >
                <User size={18} />
                <span className="d-none d-md-inline">
                  {account?.name || 'User'}
                </span>
                <Menu size={16} />
              </button>

              {/* User dropdown menu */}
              {showUserMenu && (
                <div className="dropdown-menu show position-absolute end-0 mt-2" style={{ minWidth: '200px', zIndex: 1001 }}>
                  <div className="dropdown-header">
                    <div className="fw-bold">{account?.name || 'User'}</div>
                  </div>
                  <div className="dropdown-divider"></div>
                  
                  <Link to="/configurations" className="dropdown-item d-flex align-items-center gap-2">
                    <Settings size={16} />
                    Settings
                  </Link>
                  
                  <div className="dropdown-divider"></div>
                  
                  <button
                    className="dropdown-item d-flex align-items-center gap-2 text-danger"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {(showUserMenu || showNotifications) && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 999 }}
          onClick={() => {
            setShowUserMenu(false);
            setShowNotifications(false);
          }}
        />
      )}
    </header>
  );
};

export default Header;
